import { NumberInput, type NumberInputProps } from '@mantine/core';
import type { KeyboardEvent } from 'react';
import type { Except, Merge, SetRequired } from 'type-fest';
import { useCommonInputProps } from '../../../hooks/useCommonInputProps';
import { isNumber } from '../../../utils/utilFunctions';
import type {
    Controlled_ValidatedInputProps,
    ValueOrNullableValue,
} from './types';

export type NumberOrNullableNumber<N extends boolean> = ValueOrNullableValue<
    N,
    number
>;

export type Controlled_NumberInputProps<N extends boolean> = Merge<
    Except<
        SetRequired<NumberInputProps, 'readOnly'>,
        'w' | 'styles' | 'rightSectionWidth' | 'rightSection'
    >,
    Controlled_ValidatedInputProps<N, number>
>;

export const Controlled_NumberInput = <N extends boolean>({
    nullable,
    value,
    onChange,
    label: _label,
    labelInfo,
    severity,
    helperText,
    endingIcon,
    placeholder: _placeholder,
    readOnly,
    nullableInReadOnly,
    ...props
}: Controlled_NumberInputProps<N>) => {
    const { rightSectionWidth, rightSection, label, inputStyles, placeholder } =
        useCommonInputProps({
            endingIcon,
            severity,
            helperText,
            nullable,
            onChange,
            value,
            label: _label,
            labelInfo,
            placeholder: _placeholder,
            readOnly,
            nullableInReadOnly,
        });

    const handleChange =
        onChange ?
            (newValue: string | number) => {
                if (isNumber(newValue)) {
                    onChange(Number(newValue));
                } else {
                    onChange(
                        (nullable ? null : 0) as NumberOrNullableNumber<N>
                    );
                }
            }
        :   undefined;

    const handleKeyDown =
        handleChange && value !== null ?
            (e: KeyboardEvent<HTMLInputElement>) => {
                const valueLength = value.toString().length;
                const selStart = e.currentTarget.selectionStart ?? 0;
                const selEnd = e.currentTarget.selectionEnd ?? 0;

                const lengthToCheck =
                    selStart !== selEnd ?
                        valueLength - (selEnd - selStart - 1)
                    :   valueLength;

                if (
                    (e.key === 'Backspace' || e.key === 'Delete') &&
                    lengthToCheck < 2
                ) {
                    e.preventDefault();
                    handleChange(Number.NaN);
                }
            }
        :   undefined;

    return (
        <NumberInput
            {...props}
            readOnly={readOnly}
            value={value ?? ''}
            label={label}
            placeholder={placeholder}
            rightSectionWidth={rightSectionWidth}
            rightSection={rightSection}
            styles={{ input: inputStyles }}
            onChange={handleChange}
            w={'100%'}
            onKeyDown={handleKeyDown}
        />
    );
};
